import { ExhibitorProfilesPackPrestationType, StructureType } from "@ac-expo/api-types";
import { getName, matchesBusinessSectorFilter, searchByTerms } from "@ac-expo/utils";
import { CaretUpDownIcon } from "@phosphor-icons/react";
import { noop } from "lodash";
import { observer } from "mobx-react-lite";
import { FC, useMemo, useState } from "react";
import { cn, useIsMobile } from "../../../../utils";
import { ChatCircleIcon, EnvelopeIcon, PhoneIcon } from "../../../icons";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../Select";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../Tooltip";
import UserAvatar from "../../UserAvatar";
import ProtectedLink from "../ProtectedLink";
import PromotedExhibitorsPanel, { PromotedExhibitor } from "./exhibitors/PromotedExhibitorsPanel";

const SortTypes = {
  Alphabetical: "alphabetical",
  ReversedAlphabetical: "reversed-alphabetical",
} as const;
const ScanFilterTypes = {
  All: "all",
  ScannedNow: "scanned-now",
  ScannedBefore: "scanned-before",
  NotScanned: "not-scanned",
} as const;

type StructureWithImageUrl = StructureType & {
  imgUrl: string;
  exhibitorProfilesPack: ExhibitorProfilesPackPrestationType | null;
  isPromoted?: boolean;
};
export type SearchStore = {
  searchText: string;
  setSearchText: (searchText: string) => void;
  typedSearchText: string;
  setTypedSearchText: (typedSearchText: string) => void;
};
type SearchBarProps = {
  className?: string;
  store?: SearchStore;
  id: string;
  onSubmit?: () => void;
};
export type SearchBarComponent = FC<SearchBarProps>;

export type ExponentPanelExponent = Pick<
  StructureWithImageUrl,
  | "id"
  | "businessName"
  | "description"
  | "adminId"
  | "imgUrl"
  | "exhibitorProfilesPack"
  | "isPromoted"
  | "admin"
  | "phoneNumber"
  | "businessEmail"
  | "acceptedTerms"
  | "businessSectorId"
> & {
  scannedStructures: Pick<StructureWithImageUrl["scannedStructures"][number], "eventId">[];
  businessSectorRelation?: { id: string; name: string; parentId: string | null } | null;
  products?: { id: string; name: string; description: string | null }[];
};

type ExponentPanelProps = {
  openExponentDetails?: (id: string) => void;
  openExponentConversation?: (id: string, structureId?: string) => void;
  actionGuard?: (action: () => void, fallback?: () => void, ignoreModal?: boolean) => void;
  SearchBar: SearchBarComponent;
  searchStore?: SearchStore;
  exponents: ExponentPanelExponent[];
  eventId: string;
  isEmbedded?: boolean;
  selectedBusinessSectorIds?: string[];
  businessSectorFilter?: React.ReactNode;
};

const ExponentPanel = observer(
  ({
    exponents,
    eventId,
    openExponentDetails,
    openExponentConversation,
    SearchBar,
    searchStore,
    actionGuard,
    isEmbedded,
    selectedBusinessSectorIds,
    businessSectorFilter,
  }: ExponentPanelProps) => {
    const isMobile = useIsMobile();
    const [sortType, setSortType] = useState<string>(SortTypes.Alphabetical);
    const [scanFilterType, setScanFilterType] = useState<string>(ScanFilterTypes.All);
    const [isScanFilterSelectionOpen, setIsScanFilterSelectionOpen] = useState(false);

    const onOpenExponentDetails = openExponentDetails ?? noop;
    const onOpenExponentConversation = openExponentConversation
      ? (e: React.MouseEvent<HTMLAnchorElement>, exponentId: string) => {
          e.stopPropagation();
          openExponentConversation(exponentId);
        }
      : noop;
    const onOpenScanFilterSelectionChange = (open: boolean) => {
      if (isEmbedded) {
        setIsScanFilterSelectionOpen(open);
      } else {
        actionGuard?.(() => setIsScanFilterSelectionOpen(open));
      }
    };

    const sortedExponents = useMemo(() => {
      if (sortType === SortTypes.Alphabetical) {
        return [
          ...exponents.sort((a, b) => {
            if (a.businessName === null) return 1;
            if (b.businessName === null) return -1;
            return a.businessName.localeCompare(b.businessName);
          }),
        ];
      }
      if (sortType === SortTypes.ReversedAlphabetical) {
        return [
          ...exponents.sort((a, b) => {
            if (a.businessName === null) return 1;
            if (b.businessName === null) return -1;
            return b.businessName.localeCompare(a.businessName);
          }),
        ];
      }
      return exponents;
    }, [sortType, exponents]);

    const EXPONENT_SEARCH_ID = "exponent-search-input";

    const searchedAndSortedExponents = useMemo(
      () =>
        sortedExponents.filter((exponent) => {
          if (!searchStore || searchStore.typedSearchText.trim() === "") {
            return true;
          }
          const searchableValues = [
            exponent.businessName ?? "",
            ...(exponent.products?.flatMap((p) => [p.name, p.description ?? ""]) ?? []),
          ];
          return searchByTerms(searchStore.typedSearchText, searchableValues);
        }),
      [searchStore?.typedSearchText, sortedExponents],
    );

    const filteredExponents = useMemo(() => {
      let result = searchedAndSortedExponents;

      if (selectedBusinessSectorIds && selectedBusinessSectorIds.length > 0) {
        result = result.filter((ex) => matchesBusinessSectorFilter(ex, selectedBusinessSectorIds));
      }

      switch (scanFilterType) {
        case ScanFilterTypes.ScannedNow:
          return result.filter(
            (ex) => ex.scannedStructures.filter((s) => s.eventId === eventId).length > 0,
          );
        case ScanFilterTypes.ScannedBefore:
          return result.filter(
            (ex) => ex.scannedStructures.filter((s) => s.eventId !== eventId).length > 0,
          );
        case ScanFilterTypes.NotScanned:
          return result.filter((ex) => ex.scannedStructures.length === 0);
        case ScanFilterTypes.All:
        default:
          return result;
      }
    }, [eventId, scanFilterType, searchedAndSortedExponents, selectedBusinessSectorIds]);

    const canSeePrivateInfos = useMemo(() => {
      let canSeePrivateInfos = false;
      actionGuard?.(() => (canSeePrivateInfos = true), undefined, true);
      return canSeePrivateInfos;
    }, [actionGuard]);

    const promotedExhibitors: PromotedExhibitor[] = useMemo(() => {
      return exponents
        .filter((exponent) => exponent.isPromoted)
        .map(
          (e): PromotedExhibitor => ({
            id: e.id,
            name: e.businessName ?? (e.admin ? getName(e.admin) : ""),
            imgUrl: e.imgUrl,
            description: e.description ?? "",
          }),
        );
    }, [exponents]);

    return (
      <div className="flex flex-col gap-4 p-4 md:px-6 md:py-8">
        <div className="flex md:items-center">
          <SearchBar id={EXPONENT_SEARCH_ID} className="w-full" store={searchStore} />
        </div>
        <div className="flex flex-col items-start md:flex-row md:items-center">
          <div className="flex w-full flex-row justify-between">
            <div className="flex items-center">
              <p className="text-sm font-bold text-gray-500">
                {filteredExponents.length} exposant
                {filteredExponents.length > 1 ? "s" : ""}
              </p>
            </div>
            <div className="flex items-center gap-2 pt-4 md:gap-4 md:pt-0">
              <span className="text-sm text-gray-500">Afficher</span>
              <Select
                open={isScanFilterSelectionOpen}
                onOpenChange={onOpenScanFilterSelectionChange}
                onValueChange={setScanFilterType}
                defaultValue={ScanFilterTypes.All}
              >
                <SelectTrigger className="w-auto min-w-[150px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value={ScanFilterTypes.All}>Tous les exposants</SelectItem>
                    <SelectItem value={ScanFilterTypes.ScannedNow}>
                      Scannés lors de cet événement
                    </SelectItem>
                    <SelectItem value={ScanFilterTypes.ScannedBefore}>
                      Scannés lors d&apos;événements passés
                    </SelectItem>
                    <SelectItem value={ScanFilterTypes.NotScanned}>Non scannés</SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
              <Select onValueChange={setSortType}>
                <SelectTrigger className="w-auto gap-1 border border-gray-300 hover:bg-primary-100 bg-white text-primary-500 shadow-sm [&>svg:last-child]:hidden">
                  <CaretUpDownIcon size={16} weight="bold" />
                  <span>Trier par</span>
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value={SortTypes.Alphabetical}>Ordre alphabétique (A-Z)</SelectItem>
                    <SelectItem value={SortTypes.ReversedAlphabetical}>
                      Ordre alphabétique inversé (Z-A)
                    </SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
              {businessSectorFilter}
            </div>
          </div>
        </div>
        <PromotedExhibitorsPanel
          exhibitors={promotedExhibitors}
          onExponentClick={onOpenExponentDetails}
        />
        <div>
          <div className="flex flex-col gap-4 pt-4 xl:grid xl:grid-cols-2">
            {filteredExponents.map((exponent, index) => (
              <div
                role="button"
                tabIndex={0}
                key={index}
                className={cn(
                  "flex items-center justify-between gap-4 border-b border-gray-200 p-4",
                  exponent.exhibitorProfilesPack === undefined || exponent.exhibitorProfilesPack
                    ? "cursor-pointer"
                    : "cursor-default",
                )}
                onClick={() => onOpenExponentDetails(exponent.id)}
                onKeyDown={() => onOpenExponentDetails(exponent.id)}
              >
                <div className="flex items-center">
                  <UserAvatar
                    width={20}
                    imgUrl={exponent.imgUrl}
                    name={exponent.businessName}
                    avatarImgClassName="object-cover"
                  />
                  <span className="ml-2 text-gray-700">
                    {(exponent.businessName ?? "").toUpperCase()}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  {(exponent.exhibitorProfilesPack === undefined ||
                    exponent.exhibitorProfilesPack) && (
                    <>
                      {exponent.phoneNumber && (
                        <Tooltip>
                          <TooltipContent>
                            {canSeePrivateInfos
                              ? exponent.phoneNumber
                              : "Contacter l'exposant par téléphone"}
                          </TooltipContent>
                          <TooltipTrigger>
                            <ProtectedLink
                              href={`tel:${exponent.phoneNumber}`}
                              actionGuard={actionGuard}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div role="button" tabIndex={0}>
                                <div className="flex items-center justify-center gap-1 rounded border border-blue-400 px-2 py-[3px]">
                                  <PhoneIcon width={18} height={18} fill={"#439BDD"} />
                                </div>
                              </div>
                            </ProtectedLink>
                          </TooltipTrigger>
                        </Tooltip>
                      )}
                      <Tooltip>
                        <TooltipContent>
                          {canSeePrivateInfos
                            ? exponent.businessEmail
                            : "Contacter l'exposant par mail"}
                        </TooltipContent>
                        <TooltipTrigger>
                          <ProtectedLink
                            href={`mailto:${exponent.businessEmail}`}
                            actionGuard={actionGuard}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div
                              className="flex items-center justify-center gap-1 rounded border border-blue-400 px-2 py-[3px]"
                              role="button"
                              tabIndex={0}
                            >
                              <EnvelopeIcon width={18} height={18} fill={"#439BDD"} />
                            </div>
                          </ProtectedLink>
                        </TooltipTrigger>
                      </Tooltip>
                      {exponent.acceptedTerms && !isEmbedded && (
                        <Tooltip>
                          <TooltipContent>
                            Contacter l&apos;exposant via la messagerie
                          </TooltipContent>
                          <TooltipTrigger>
                            <ProtectedLink
                              actionGuard={actionGuard}
                              onClick={(e) => onOpenExponentConversation(e, exponent.adminId)}
                            >
                              <div className="flex items-center justify-center gap-1 rounded border border-blue-400 px-2 py-[3px]">
                                <ChatCircleIcon width={18} height={18} fill={"#439BDD"} />
                              </div>
                            </ProtectedLink>
                          </TooltipTrigger>
                        </Tooltip>
                      )}
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  },
);

export default ExponentPanel;
